import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import publicIp from 'public-ip';
import {
  makeStyles,
  Theme,
  useMediaQuery,
  useTheme,
  IconButton
} from '@material-ui/core';
import { ChevronLeft } from '@carbon/icons-react';
import {
  CapaErrorCode,
  useFetchQuotationPartnerQuery,
  CapaQuotationPartnerStatus
} from '@generated/graphql';
import { getToken } from '@utils/storage';
import { colorSet, useSnackbar } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import LoadingPresenter from '@components/Loading';
import CompanyAddress from './components/CompanyAddress';
import CompanyCertificate from './components/CompanyCertificate';
import CompanyEmployees from './components/CompanyEmployees';
import CompanyEquipment from './components/CompanyEquipment';
import CompanyFactoryAddress from './components/CompanyFactoryAddress';
import CompanyFoundedIn from './components/CompanyFoundedIn';
import CompanyGallery from './components/CompanyGallery';
import CompanyIntroduction from './components/CompanyIntroduction';
import CompanyName from './components/CompanyName';
import CompanyPatent from './components/CompanyPatent';
import CompanyPortfolio from './components/CompanyPortfolio';
import CompanyInfoNavigation from './components/CompanyInfoNavigation';
import CompanyServiceHour from './components/CompanyServiceHour';
import CompanyStatistic from './components/CompanyStatistic';
import CompanyInfoContextProvider from './context/CompanyInfoContext';
import CompanyReview from './components/CompanyReview';
import CompanyCover from './components/CompanyCover';

interface CompanyInfoViewProps {
  partnerId: string;
  isRequest?: boolean;
  tabletSize?: number;
  isAuth?: boolean;
}

interface CompanyInfoViewStyleProps {
  showNavigation: boolean;
  isRequest?: boolean;
  isAuth: boolean;
  isDashboard: boolean;
}

const CompanyInfoView: React.FunctionComponent<CompanyInfoViewProps> = ({
  partnerId,
  isAuth = true,
  isRequest = false
}) => {
  const isDashboard = window.location.pathname.includes('/dashboard');
  const theme = useTheme();
  const matchesMd = useMediaQuery(theme.breakpoints.down('md'));
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [showNavigation, setShowNavigation] = useState<boolean>(false);
  const obeserveTarget = useRef<HTMLDivElement | null>(null);
  const observer = useRef<IntersectionObserver | null>();
  const {
    companyInfoViewContainer,
    navigation,
    mobileHeader,
    companyInfoWrapper,
    companyNameWrapper,
    companyNameContainer,
    requestDiv
  } = useStyles({
    showNavigation,
    isAuth,
    isDashboard
  });
  const [ip, setIp] = useState<string | null>(null);

  const handleGoBack = () => {
    if (history.length > 1) {
      history.goBack();
      return;
    }
    history.push('/dashboard');
  };

  useEffect(() => {
    publicIp.v4().then((ip) => {
      setIp(ip);
    });
  }, []);

  const { data, loading } = useFetchQuotationPartnerQuery({
    variables: {
      quotationPartnerId: partnerId as string
    },
    skip: !getToken() && !ip,
    context: {
      headers: {
        ip
      }
    },
    onError: () => {
      enqueueSnackbar('요청하신 파트너의 정보를 찾을 수 없습니다.');
      history.replace('/companyInfo/notfound');
    },
    onCompleted: (result) => {
      if (
        result?.fetchQuotationPartner?.result === CapaErrorCode.Success &&
        result.fetchQuotationPartner?.data?.status ===
          CapaQuotationPartnerStatus.Approved
      )
        return;

      enqueueSnackbar('요청하신 파트너의 정보를 찾을 수 없습니다.');
      history.replace('/companyInfo/notfound');
    }
  });

  const observerCallback: IntersectionObserverCallback = useCallback(
    (entries) => {
      if (entries.length > 0) {
        if (!entries[0].isIntersecting && showNavigation === false) {
          return setShowNavigation(true);
        }
        return setShowNavigation(false);
      }
    },
    [showNavigation]
  );

  useEffect(() => {
    const html = document.querySelector('html');

    if (html) {
      (html.style as CSSStyleDeclaration & {
        scrollPaddingTop?: string;
      }).scrollPaddingTop = '121px';
      html.style.scrollBehavior = 'smooth';
    }

    return () => {
      if (html) {
        (html.style as CSSStyleDeclaration & {
          scrollPaddingTop?: string;
        }).scrollPaddingTop = '61px';
        html.style.scrollBehavior = 'initial';
      }
    };
  }, []);

  useEffect(() => {
    if (obeserveTarget.current) {
      observer.current = new IntersectionObserver(observerCallback, {});
      observer.current.observe(obeserveTarget.current);
    }
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
    //eslint-disable-next-line
  }, [loading]);

  if (loading) return <LoadingPresenter open={loading} />;

  return (
    <CompanyInfoContextProvider
      value={{
        partnerId: partnerId,
        partnerInfo: data?.fetchQuotationPartner.data ?? undefined
      }}
    >
      {isAuth && matchesMd && (
        <div
          className={clsx([
            mobileHeader,
            {
              isRequest
            }
          ])}
        >
          <IconButton onClick={handleGoBack}>
            <ChevronLeft size={20} color={colorSet.gray900} />
          </IconButton>
          {isRequest && (
            <CustomizedTypography
              className="organizationName"
              variant="body1"
              fontWeight="500"
              color="textPrimary"
            >
              {data?.fetchQuotationPartner.data?.organizationName}
            </CustomizedTypography>
          )}
        </div>
      )}
      <div className={isRequest ? requestDiv : ''}>
        <div className={companyNameWrapper}>
          <div className={companyNameContainer}>
            <div className="companyName-section">
              <CompanyCover />
              <CompanyName ref={obeserveTarget} />
            </div>
          </div>
        </div>
        <div className={companyInfoWrapper}>
          {!isRequest && (
            <div className={navigation}>
              <CompanyInfoNavigation />
            </div>
          )}
          <div className={companyInfoViewContainer}>
            <div className="company-profile-section">
              {matchesMd && <ProfileExtraSection />}
              <div className="company-Info-detail">
                <CompanyIntroduction isRequest={isRequest} />
                <CompanyPortfolio />
                <CompanyGallery />
                <CompanyEquipment />
                <CompanyCertificate />
                <CompanyPatent />
                <CompanyReview data={data?.fetchQuotationPartner.data} />
              </div>
            </div>
            {!matchesMd && <ProfileExtraSection />}
          </div>
        </div>
      </div>
    </CompanyInfoContextProvider>
  );
};

export default CompanyInfoView;

export const ProfileExtraSection = () => {
  return (
    <div className="company-Info-extra">
      <CompanyStatistic />
      <CompanyFoundedIn />
      <CompanyEmployees />
      <CompanyAddress />
      <CompanyFactoryAddress />
      <CompanyServiceHour />
    </div>
  );
};

const useStyles = makeStyles<Theme, CompanyInfoViewStyleProps>((theme) => ({
  requestDiv: {
    backgroundColor: `${colorSet.gray000}`,
    boxShadow: '0px 16px 32px 8px rgba(0, 0, 0, 0.02)',
    borderRadius: 16
  },
  companyNameWrapper: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    borderBottom: `1px solid ${colorSet.gray400}`,
    [theme.breakpoints.down('md')]: {
      paddingTop: ({ isAuth }) => (isAuth ? 48 : 0)
    }
  },

  companyNameContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    width: '100%',

    '& .companyName-section': {
      maxWidth: 1024,
      height: 392,
      width: '100%',
      [theme.breakpoints.down('md')]: {
        height: 564
      },
      [theme.breakpoints.down('xs')]: {
        height: ({ isDashboard }) => (isDashboard ? '475px' : '532px'),
        maxWidth: '100%'
      }
    }
  },

  companyInfoWrapper: {
    display: 'flex',
    flexDirection: 'column',
    background: `${colorSet.gray200}`,
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
      borderRadius: ({ isDashboard }) => (isDashboard ? '0 0 16px 16px' : '0px')
    }
  },
  companyInfoViewContainer: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    width: '100%',
    marginTop: 16,
    paddingBottom: ({ isDashboard }) => (isDashboard ? '0px' : '100px'),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      maxWidth: 626,
      margin: '16px auto 0 auto'
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      width: '100%'
    },
    '& .company-profile-section': {
      display: 'flex',
      justifyContent: 'center',
      gap: '16px',
      flexDirection: 'column',

      '& .company-Info-detail': {
        width: 626,
        background: '#FFFFFF',
        borderRadius: 12,
        padding: '0px 20px',
        [theme.breakpoints.down('md')]: {
          width: '100%'
        },
        [theme.breakpoints.down('xs')]: {
          width: '100%',
          padding: 0,
          borderRadius: '0px'
        }
      }
    },
    '& .company-Info-extra': {
      position: 'sticky',
      background: '#FFFFFF',
      borderRadius: 12,
      padding: 20,
      top: ({ showNavigation }) => (showNavigation ? 61 + 16 + 60 : 61 + 40),
      width: '100%',
      maxWidth: 382,
      height: 'fit-content',
      transition: 'all 0.2s ease-in-out',
      [theme.breakpoints.down('md')]: {
        position: 'inherit',
        maxWidth: 626
      },
      [theme.breakpoints.down('xs')]: {
        borderRadius: 0,
        padding: 16,
        maxWidth: '100%',
        position: 'inherit'
      }
    }
  },
  navigation: {
    display: 'flex',
    position: 'fixed',
    alignItems: 'center',
    justifyContent: 'center',
    top: ({ showNavigation }) => (showNavigation ? 61 : -60),
    left: 0,
    width: '100%',
    height: '60px',
    backgroundColor: colorSet.gray000,
    zIndex: 2,
    borderBottom: `1px solid ${colorSet.gray400}`,
    transition: 'all 0.2s ease-in-out',
    pointerEvents: ({ showNavigation }) => (showNavigation ? 'all' : 'none'),
    [theme.breakpoints.down('md')]: {
      display: 'none'
    }
  },
  mobileHeader: {
    position: 'fixed',
    top: 0,
    width: '100%',
    height: 48,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: colorSet.gray000,
    zIndex: 2,
    '&.isRequest': {
      visibility: 'hidden'
    }
  }
}));
